import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { ANALYTICS_PAGE_NAMES } from 'dpl/constants/analytics';
import SmartImage from 'dpl/components/SmartImage';
import SmartLink from 'dpl/components/SmartLink';
import Icon from 'dpl/common/components/Icon';
import ClampedMultiLineText from 'dpl/common/components/ClampedMultiLineText';
import SectionViewTracker from 'dpl/common/components/SectionViewTracker';

import { usePuppyData } from '../hooks/usePuppyData';
import Section from './Section';

function BreederAttributeItem({ attribute }) {
  return (
    <li className="flex">
      <Icon name={`fetch-${attribute.icon}`} width="32px" height="32px" />
      <div className="ml4">
        <p className="fw-medium mb1">{attribute.title}</p>
        <ClampedMultiLineText
          className="secondary"
          text={attribute.description}
          numOfLines={3}
          lineHeight={21.44}
          moreText="Read more"
          lessText="Show less"
          fadeToButton
        />
      </div>
    </li>
  );
}

BreederAttributeItem.propTypes = {
  attribute: PropTypes.shape({
    icon: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string
  }).isRequired
};

function BreederAttributesList({ breeder, columnLayout }) {
  const mid = Math.ceil(breeder.attributes.length / 2);

  return (
    <div
      className={classnames({
        'mt0 mt16-md': columnLayout
      })}
    >
      <h5 className="heading-5 mb6">{breeder.first_name}&apos;s experience</h5>
      {columnLayout ? (
        <ul className="MeetPuppyBreeder__attributes row wider-gutters">
          {[
            breeder.attributes.slice(0, mid),
            breeder.attributes.slice(mid, breeder.attributes.length)
          ].map((col, colIdx) => (
            <div
              key={colIdx}
              className="MeetPuppyBreeder__attributes--col col-md-6 flex flex-column"
            >
              {col.map((attr, idx) => (
                <BreederAttributeItem key={idx} attribute={attr} />
              ))}
            </div>
          ))}
        </ul>
      ) : (
        <ul className="MeetPuppyBreeder__attributes flex flex-column">
          {breeder.attributes.map((attr, idx) => (
            <BreederAttributeItem key={idx} attribute={attr} />
          ))}
        </ul>
      )}
    </div>
  );
}

BreederAttributesList.propTypes = {
  breeder: PropTypes.shape({
    first_name: PropTypes.string.isRequired,
    attributes: PropTypes.arrayOf(
      PropTypes.shape({
        description: PropTypes.string,
        icon: PropTypes.string,
        title: PropTypes.string
      })
    )
  }).isRequired,
  columnLayout: PropTypes.bool
};

BreederAttributesList.defaultProps = {
  columnLayout: false
};

export default function MeetPuppyBreeder() {
  const { puppy, breeder } = usePuppyData();
  const hasProfilePhoto = Boolean(breeder.profile_photo_url);
  const hasBreederAttributes = breeder.attributes.length > 0;

  return (
    <SectionViewTracker
      name={ANALYTICS_PAGE_NAMES.PUPPY_PROFILE}
      category="puppy-profile-breeder"
    >
      <Section className="MeetPuppyBreeder">
        <Section.Heading className="mb8 mb14-md">
          Meet {puppy.name}’s breeder
        </Section.Heading>
        <div className="row wider-gutters">
          {hasProfilePhoto && (
            <div className="col-md-6">
              <div className="aspect-ratio aspect-ratio--6x4 mb10 mb0-md">
                <div className="aspect-ratio--object">
                  <SmartImage
                    Tag="div"
                    className="w-100 h-100 cover bg-center"
                    wrapperOnlyClassName="br3 overflow-hidden"
                    src={breeder.meet_the_breeder_photo_url}
                  />
                </div>
              </div>
            </div>
          )}
          <div className="col-md-6">
            <div
              className={classnames('mb12', {
                'mb16-md': hasProfilePhoto && hasBreederAttributes,
                'mb0-md': !hasProfilePhoto || !hasBreederAttributes
              })}
            >
              <h5 className="heading-5">
                Hi, I&apos;m {breeder.first_name} from {breeder.program_name}
              </h5>
              <div className="flex items-center mt2">
                <Icon
                  name="fetch-badge-paw"
                  className="icon-branded"
                  width="32px"
                  height="32px"
                />
                <p className="f3 secondary ml1">Certified Good Dog Breeder</p>
              </div>
              <ClampedMultiLineText
                className="f3 secondary mt4 mb10"
                text={breeder.description}
                numOfLines={6}
                lineHeight={24}
                moreText="Read more"
                lessText="Show less"
                fadeToButton
              />
              <SmartLink
                to={breeder.profile_url}
                className="db dib-ns tc bg-pale-blue stone-700 fw-medium pv3 ph12 br1"
                target="_blank"
              >
                View {breeder.first_name}&apos;s profile
              </SmartLink>
            </div>
            {hasProfilePhoto &&
              hasBreederAttributes &&
              breeder.attributes.length <= 3 && (
                <BreederAttributesList breeder={breeder} />
              )}
          </div>
          {!hasProfilePhoto && hasBreederAttributes && (
            <div className="col-md-6">
              <BreederAttributesList breeder={breeder} />
            </div>
          )}
        </div>
        {hasProfilePhoto && breeder.attributes.length > 3 && (
          <BreederAttributesList breeder={breeder} columnLayout />
        )}
      </Section>
    </SectionViewTracker>
  );
}
